import React, { useEffect, useState } from 'react';
import { History, Loader2, RefreshCw, UserRound } from 'lucide-react';
import { api } from '../context/AuthContext';
import { teamMembers } from '../data/teamData';

const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-wider border rounded-sm transition-colors disabled:opacity-50';
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const STIL = {
  Zugestellt: 'bg-emerald-50 text-emerald-700', 'Geöffnet': 'bg-emerald-100 text-emerald-800', Versendet: 'bg-sky-50 text-sky-700',
  'Verzögert': 'bg-amber-50 text-amber-800', Abgewiesen: 'bg-rose-50 text-rose-700', Spam: 'bg-rose-50 text-rose-700',
  Blockiert: 'bg-rose-50 text-rose-700', 'Ungültig': 'bg-rose-50 text-rose-700',
};

const VerlaufZeile = ({ l }) => (l.art === 'adresse_geaendert' ? (
  <li className="px-4 py-2 text-xs border-t border-gray-100 bg-amber-50/40 text-[#34536b]" data-testid={`verlauf-${l.log_id}`}>
    <span className="font-medium text-[#1B3A5C]">Adresse geändert</span> · {fmt(l.gesendet_am)} · <span className="font-mono line-through text-rose-700">{l.alt}</span> → <span className="font-mono text-emerald-700">{l.neu}</span>{l.von ? ` · durch ${l.von}` : ''}
  </li>
) : (
  <li className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-t border-gray-100 items-center" data-testid={`verlauf-${l.log_id}`}>
    <span className="col-span-4 md:col-span-3 text-[#1B3A5C] font-medium">{l.art === 'erinnerung' ? 'Erinnerung' : 'Einladung'}</span>
    <span className="col-span-8 md:col-span-3 text-[#34536b]">{fmt(l.gesendet_am)}</span>
    <span className="col-span-6 md:col-span-3">
      <span className={`px-2 py-0.5 rounded-sm text-[10px] tracking-wide ${STIL[l.zustellung] || 'bg-gray-100 text-gray-600'}`} title={l.error || ''} data-testid={`verlauf-status-${l.log_id}`}>
        {l.versand === 'fehlgeschlagen' ? 'Senden fehlgeschlagen' : l.zustellung}
      </span>
      {l.zustellung_am && <span className="block text-[10px] text-[#7a8a99] mt-0.5">{fmt(l.zustellung_am)}</span>}
    </span>
    <span className="col-span-6 md:col-span-3 text-[10px] text-[#7a8a99] text-right">Link bis {fmt(l.link_bis)}</span>
  </li>
));

export const ZugangVerlauf = ({ email, reload = 0 }) => {
  const [items, setItems] = useState(null);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const laden = async () => {
    setBusy(true);
    try { const r = await api.get(`/cases/zugang/verlauf?email=${encodeURIComponent(email)}`); setItems(r.data.items); }
    catch { setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (reload) laden(); }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => { if (!offen && items === null) laden(); setOffen(!offen); };
  return (
    <div className="border-t border-gray-100" data-testid="zugang-verlauf">
      <div className="flex items-center gap-2 px-4 py-2">
        <button type="button" onClick={toggle} className={`${btn} border-gray-200 text-[#1B3A5C] hover:border-[#1B3A5C]`} data-testid="zugang-verlauf-toggle" aria-expanded={offen}>
          <History className="w-3 h-3" /> EINLADUNGS-VERLAUF {items ? `(${items.length})` : ''}
        </button>
        {offen && <button type="button" onClick={laden} disabled={busy} className="text-[#7a8a99] hover:text-[#1B3A5C]" title="Zustellstatus aktualisieren" data-testid="zugang-verlauf-refresh">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</button>}
      </div>
      {offen && items && (
        <ul data-testid="zugang-verlauf-liste">
          {items.map((l) => <VerlaufZeile key={l.log_id} l={l} />)}
          {items.length === 0 && <li className="px-4 py-3 text-xs text-[#7a8a99] border-t border-gray-100">Noch keine Einladungs-Mails protokolliert.</li>}
        </ul>
      )}
    </div>
  );
};

export const AnsprechpersonSelect = ({ email, value, onSaved, onError }) => {
  const [busy, setBusy] = useState(false);
  const speichern = async (ap) => {
    setBusy(true);
    try { await api.patch('/cases/zugang/ansprechperson', { email, ansprechperson: ap }); onSaved(ap); }
    catch (e) { onError(e?.response?.data?.detail || 'Ansprechperson konnte nicht gespeichert werden'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-wider text-[#7a8a99] flex items-center gap-1"><UserRound className="w-3 h-3" />ANSPRECHPERSON</span>
      <select value={value || ''} disabled={busy} onChange={(e) => speichern(e.target.value)} data-testid="panel-ansprechperson-select"
        className="text-xs border border-gray-200 px-2 py-1 bg-white focus:border-[#b8893d] focus:outline-none">
        <option value="">– keine –</option>
        {teamMembers.filter((m) => m.email).map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
      </select>
      {busy && <Loader2 className="w-3 h-3 animate-spin text-[#7a8a99]" />}
    </div>
  );
};
